import { jsPDF } from "jspdf";
import QRCode from 'qrcode';
import { Acorn } from '../../models/acorn';
import logoUrl from '../../assets/logo.png';

type Options = {
    returnBase64?: boolean;
    returnBlob?: boolean;
    includeHeader?: boolean;
    // Optional list of badges for name resolution
    badges?: Array<{ badgeId?: string; id?: string; badgeName?: string }>;
};

// Generate a "coupon book" PDF similar to the existing layout, but with a header
// above each coupon (outside the dotted border) showing the point value and
// optional badge name if available on the acorn object.
export async function generateCouponBookPDF(acorns: Acorn[], opts: Options = {}): Promise<string | Blob | void> {
    const { returnBase64 = false, returnBlob = false, includeHeader = false } = opts;

    const generateQRCode = async (code: string): Promise<string> => {
        return await QRCode.toDataURL(code, {
            errorCorrectionLevel: 'M',
            margin: 1,
            width: 100,
            color: { dark: '#000000', light: '#ffffff' },
        });
    };

    const loadImage = (src: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
            img.src = src as unknown as string;
        });

    // Removed old getBadgeName helper (now replaced by buildHeader logic)

    const getBadgeImageUrl = (acorn: any): string | undefined => {
        try {
            const url = acorn?.badgeImageUrl
                || acorn?.badge?.badgeImageUrl
                || acorn?.levelImageUrl
                || acorn?.badgeLevel?.levelImageUrl
                || acorn?.badge_icon
                || acorn?.badgeIcon;
            return typeof url === 'string' && url ? url : undefined;
        } catch {
            return undefined;
        }
    };

    // Build header string per requirements:
    // 1. Don’t render 0 pts if points == 0
    // 2. Render badge XP groups: "25 badge XP"; if multiple badges share same xpAmount -> "25 badge XP all: (Badge A, Badge B)"
    // 3. If both points and badge XP, separate with bullet •
    // 4. Multiple differing xp groups: comma-delimit
    // 5. If badge names missing, fallback to first 8 of badgeId
    const nameLookup = new Map<string, string>();
    (opts.badges || []).forEach(b => {
        const key = String((b.badgeId || b.id || '')).toLowerCase();
        const val = (b.badgeName || '').trim();
        if (key && val) nameLookup.set(key, val);
    });

    const buildHeader = (acorn: any): string | undefined => {
        const rawPoints = Number(acorn?.points);
        const showPoints = !isNaN(rawPoints) && rawPoints > 0;
        const pointsPart = showPoints ? `${rawPoints} ${rawPoints === 1 ? 'pt' : 'pts'}` : '';

        const badgeXps: any[] = Array.isArray(acorn?.acornBadgeXps) ? acorn.acornBadgeXps : [];
        if (!badgeXps.length && !showPoints) return undefined; // nothing to show

        // Group by xpAmount
        const groups = new Map<number, Array<string>>();
        for (const entry of badgeXps) {
            const xp = Number(entry?.xpAmount);
            if (isNaN(xp) || xp <= 0) continue;
            // Resolve badge name: prefer provided badges map, then entry badge.name
            const byIdName = entry?.badgeId ? nameLookup.get(String(entry.badgeId).toLowerCase()) : undefined;
            const entryName = entry?.badge?.badgeName || entry?.badgeName;
            const name = (typeof byIdName === 'string' && byIdName.trim())
                ? byIdName.trim()
                : (typeof entryName === 'string' && entryName.trim()) ? entryName.trim() : undefined;
            const arr = groups.get(xp) || [];
            if (name) arr.push(name);
            groups.set(xp, arr);
        }

        const badgeParts: string[] = [];
        for (const [xp, names] of groups.entries()) {
            // Deduplicate, stable sort by name for consistency
            const uniq = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
            if (uniq.length > 1) {
                badgeParts.push(`${xp} badge XP all: (${uniq.join(', ')})`);
            } else if (uniq.length === 1) {
                badgeParts.push(`${xp} badge XP (${uniq[0]})`);
            } else {
                // No known names for this xp group; still render amount only
                badgeParts.push(`${xp} badge XP`);
            }
        }

        // Stable ordering: highest XP first (optional - keeps combining predictable)
        badgeParts.sort((a, b) => {
            const ax = Number(a.split(' ')[0]);
            const bx = Number(b.split(' ')[0]);
            return bx - ax;
        });

        const badgeXpPart = badgeParts.join(', ');

        if (pointsPart && badgeXpPart) return `${pointsPart} • ${badgeXpPart}`;
        return pointsPart || badgeXpPart || undefined;
    };

    try {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'in', format: [8.5, 11] });

        let logoImg: HTMLImageElement | null = null;
        try { logoImg = await loadImage(logoUrl as unknown as string); } catch { logoImg = null; }

        // Layout constants (mirror the existing CouponPDF.tsx)
        const couponWidth = 4;
        const couponHeight = 2;
        const margin = 0.25;
        const columns = 2;
        const rows = 5;
        const pageInnerWidth = 8.5;
        const pageInnerHeight = 11;

        const startX = (pageInnerWidth - columns * couponWidth) / 2;
        const startY = (pageInnerHeight - rows * couponHeight) / 2;

        const totalPerPage = columns * rows;
        const totalPages = Math.ceil(acorns.length / totalPerPage);

        for (let page = 0; page < totalPages; page++) {
            if (page > 0) doc.addPage();

            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < columns; col++) {
                    const index = page * totalPerPage + row * columns + col;
                    if (index >= acorns.length) continue;

                    const acorn = acorns[index] as any;
                    const x = startX + col * couponWidth;
                    const y = startY + row * couponHeight;

                    // Header text outside the dotted border (above the coupon)
                    // Header text outside the dotted border, in the gap ABOVE this coupon
                    // Build header using new logic instead of previous simplistic approach
                    const header = includeHeader ? buildHeader(acorn) : undefined;

                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(7);
                    doc.setTextColor(20, 20, 20);

                    // Centered with respect to the coupon’s dotted rect
                    const rectWidth = couponWidth - margin / 2;
                    const rectHeight = couponHeight - margin / 2;
                    const headerCenterX = x + rectWidth / 2;

                    // There is a small vertical gap between the previous row's bottom
                    // and this coupon's top: gapBetweenRows = couponHeight - rectHeight.
                    const gapBetweenRows = couponHeight - rectHeight; // 0.125"

                    // Default: put the header in the middle of that gap
                    let headerY = (y - gapBetweenRows / 2) + .03;

                    if (header) {
                        doc.text(header, headerCenterX, headerY, { align: 'center' });
                    }

                    // Dotted coupon border
                    doc.setDrawColor(0, 0, 0);
                    doc.setLineWidth(0.01);
                    doc.setLineDashPattern([0.05, 0.05], 0);
                    doc.rect(x, y, couponWidth - margin / 2, couponHeight - margin / 2, 'S');

                    // QR code and content mirror existing
                    const qrDataUrl = await generateQRCode(acorn.code);
                    const qrSize = 1.25;
                    const codeFontSize = 12;
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(codeFontSize);
                    const codeTextWidth = doc.getTextWidth(acorn.code);
                    const codeLeft = x + 0.25;
                    const codeCenter = codeLeft + codeTextWidth / 2;
                    let qrX = codeCenter - qrSize / 2 - 0.06;
                    const minQrX = x + 0.15;
                    const maxQrX = x + (couponWidth - margin / 2) - qrSize - 0.25;
                    qrX = Math.max(minQrX, Math.min(maxQrX, qrX));
                    const qrY = y + 0.26;
                    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

                    // Code text
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(codeFontSize);
                    doc.text(acorn.code, codeLeft, y + 0.28);

                    // Right side content: badge/logo + description
                    const rightStartX = qrX + qrSize + 0.10;
                    const rightWidth = (x + (couponWidth - margin / 2)) - rightStartX - 0.15;
                    let logoHeight = 0.33;
                    let logoWidth = 0.33;
                    const centerY = qrY + qrSize / 2;
                    const logoY = centerY - logoHeight / 2;
                    // Prefer coupon's badge image if available; fallback to app logo
                    const badgeImageUrl = getBadgeImageUrl(acorn);
                    if (badgeImageUrl) {
                        try {
                            const badgeImg = await loadImage(badgeImageUrl);
                            logoWidth = (badgeImg.width / badgeImg.height) * logoHeight;
                            doc.addImage(badgeImg, 'PNG', rightStartX, logoY, logoWidth, logoHeight);
                        } catch {
                            if (logoImg) {
                                logoWidth = (logoImg.width / logoImg.height) * logoHeight;
                                doc.addImage(logoImg, 'PNG', rightStartX, logoY, logoWidth, logoHeight);
                            }
                        }
                    } else if (logoImg) {
                        logoWidth = (logoImg.width / logoImg.height) * logoHeight;
                        doc.addImage(logoImg, 'PNG', rightStartX, logoY, logoWidth, logoHeight);
                    }

                    let description = (acorn.acornDescription || '').trim();
                    const textX = rightStartX + (logoImg ? logoWidth + 0.03 : 0);
                    const textMaxWidth = rightWidth - (logoImg ? logoWidth + 0.12 : 0);
                    if (description) {
                        const approxMaxChars = 48;
                        if (description.length > approxMaxChars) {
                            description = description.slice(0, approxMaxChars - 1).trimEnd() + '…';
                        }
                        doc.setFont('times', 'normal');
                        doc.setFontSize(10);
                        doc.setTextColor(20, 20, 20);
                        const oneLine = (doc.splitTextToSize(description, textMaxWidth) as string[])[0] || '';
                        const textY = centerY + 0.05;
                        doc.text(oneLine, textX, textY);
                    }

                    // Footer recycle note
                    const footer1 = 'Recycle This Coupon For A Chance To Win Additional Rewards.';
                    const footer2 = 'Recycle At: Puma Lounge (Room 202) Or Your Teacher';
                    doc.setFont('times', 'normal');
                    doc.setFontSize(8.5);
                    doc.setTextColor(30, 30, 30);
                    const footerY1 = qrY + qrSize + 0.07;
                    const footerY2 = footerY1 + 0.16;
                    const centerX = x + (couponWidth - margin / 2) / 2;
                    doc.text(footer1, centerX, footerY1, { align: 'center' });
                    doc.text(footer2, centerX, footerY2, { align: 'center' });
                }
            }
        }

        if (returnBase64) return doc.output('datauristring');
        if (returnBlob) return doc.output('blob');

        doc.save(`couponbook-${Date.now()}.pdf`);
    } catch (err) {
        console.error('CouponBook PDF generation error:', err);
        throw err;
    }
}
